import { useState } from "react";
import { Outlet, Link, matchPath, useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client/react";
import { Home, Lock, LogOut } from "lucide-react";

import { LOGOUT } from "@/graphql/mutations/admin";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { CelebrationProvider } from "@/components/celebrations";
import { HolidayDecorations } from "@/components/holidays";
import { useAuth } from "@/lib/auth/AuthContext";
import { LegalFooter } from "@/components/legal/legal-footer";
import { NotificationBell } from "./notification-bell";
import { ParentExitDialog } from "./parent-exit-dialog";

/**
 * The learner's shell. A separate layout rather than the grown-up one with items
 * hidden: a greyed-out "Administravimas" is exactly what invites poking at it.
 * There is no navigation here at all — only this learner's own world.
 */
export function KidLayout() {
  const { student, canExitToOwner } = useAuth();
  const { pathname } = useLocation();
  const atHome = pathname === "/";
  const solving = matchPath("/sessions/:id/solve", pathname) !== null;
  const [pinOpen, setPinOpen] = useState(false);
  const [logout] = useMutation(LOGOUT);

  const initials = (student?.name ?? "")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      window.location.assign("/auth/login");
    }
  };

  return (
    <CelebrationProvider>
      {/* Same surface as the grown-up shell, and the same full width — the
          learner's pages are the ones that need the room most. */}
      <div className="min-h-screen bg-background">
        <header className="flex items-center gap-3 border-b px-4 py-3 md:px-6">
          {/* Your own face takes you to your own page — the one place in the
              shell that is about the learner rather than the work. */}
          <Link to="/profile" className="flex items-center gap-3" title="Mano profilis">
            <Avatar className="h-12 w-12 border-2 border-white shadow-sm">
              {student?.avatarUrl && <AvatarImage src={student.avatarUrl} alt="" />}
              <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                {initials || "?"}
              </AvatarFallback>
            </Avatar>
            <span className="text-lg font-bold tracking-tight">{student?.name}</span>
          </Link>

          <div className="ml-auto flex items-center gap-1">
            {/* First in the cluster, and the only thing here that is *for* the
                learner — the rest of this corner is leaving. */}
            <NotificationBell />
            {!atHome && (
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground/60 hover:text-muted-foreground"
                asChild
                aria-label="Į pradžią"
                title="Į pradžią"
              >
                <Link to="/">
                  <Home className="h-4 w-4" />
                </Link>
              </Button>
            )}
            {/* Small, quiet, and in the corner. Leaving should be boring. */}
            {canExitToOwner ? (
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground/60 hover:text-muted-foreground"
                onClick={() => setPinOpen(true)}
                aria-label="Tėvams"
                title="Tėvams"
              >
                <Lock className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground/60 hover:text-muted-foreground"
                onClick={handleLogout}
                aria-label="Atsijungti"
                title="Atsijungti"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            )}
          </div>
        </header>

        <main className="p-4 pb-16 md:p-6">
          <Outlet />
        </main>

        {/* The same legal footer as every other shell — the privacy policy has
            to be reachable from every page, a learner's included. The solve
            page pins its navigation to the bottom of the screen, so there the
            footer keeps clear of that bar instead of sitting underneath it. */}
        <LegalFooter fullWidth className={solving ? "mb-28" : ""} />
      </div>

      <ParentExitDialog open={pinOpen} onOpenChange={setPinOpen} />
      <HolidayDecorations />
      <Toaster />
    </CelebrationProvider>
  );
}

